import {
  Alert,
  Badge,
  Box,
  Button,
  Drawer,
  FileUpload,
  Flex,
  Heading,
  HStack,
  Portal,
  Table,
  Text,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { LuDownload, LuUpload } from 'react-icons/lu'

import {
  downloadContactImportTemplate,
  importContacts,
  previewContactImport,
} from '@/api/settings.api'
import type {
  ContactImportPreviewResponse,
  ContactImportResultDto,
  ContactImportRowDto,
  ContactImportRowStatus,
} from '@/api/dto/settings.dto'
import { toast } from '@/components/ui/toaster/toasterHelpers'
import { getErrorMessage } from '@/helper/apiError.helpers'
import { triggerBlobDownload } from '@/helper/common.helper'

const XLSX_ACCEPT: Record<string, string[]> = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
}

/**
 * Per-status label and colour. The five states are kept visually distinct rather than collapsed
 * into "ok / error" because the SIB's next action differs for each: pick from the dropdown for an
 * unknown bank, choose between same-named banks for an ambiguous one, fix a field for an invalid
 * one — and do nothing at all for a duplicate.
 */
const STATUS_META: Record<
  ContactImportRowStatus,
  { label: string; committedLabel: string; palette: string }
> = {
  MATCHED: { label: 'Wird importiert', committedLabel: 'Importiert', palette: 'green' },
  DUPLICATE: { label: 'Übersprungen', committedLabel: 'Übersprungen', palette: 'gray' },
  UNKNOWN_BANK: {
    label: 'Unbekannter Leistungsempfänger',
    committedLabel: 'Nicht importiert',
    palette: 'red',
  },
  AMBIGUOUS: { label: 'Nicht eindeutig', committedLabel: 'Nicht importiert', palette: 'orange' },
  INVALID: { label: 'Ungültige Daten', committedLabel: 'Nicht importiert', palette: 'red' },
}

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called after a commit that created at least one contact, so the list can reload. */
  onImported: () => void
}

export function ContactImportDrawer({ open, onOpenChange, onImported }: Props) {
  const [downloading, setDownloading] = useState(false)
  const [checking, setChecking] = useState(false)
  const [importing, setImporting] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<ContactImportPreviewResponse | null>(null)
  const [result, setResult] = useState<ContactImportResultDto | null>(null)
  /**
   * Remount key for the file picker. Both the native input and Chakra's FileUpload keep their own
   * selection, so after "Abbrechen" re-picking the *same* file fired no change event and the
   * drawer sat there doing nothing. Bumping the key gives a fresh, empty picker every time.
   */
  const [uploadKey, setUploadKey] = useState(0)

  function reset() {
    setFile(null)
    setPreview(null)
    setResult(null)
    setUploadKey(key => key + 1)
  }

  /**
   * Clear everything whenever the drawer closes, on every path.
   *
   * Chakra only fires `Drawer.Root.onOpenChange` for its own close interactions (backdrop, Esc,
   * close trigger) — not when the parent flips the `open` prop, which is what our "Schließen"
   * button and the auto-close after a clean import do. Pairing `reset()` with each
   * `onOpenChange(false)` call site was easy to forget (and was forgotten), leaving the previous
   * import's result table on screen the next time the drawer opened. Keying off `open` covers
   * every close, including ones added later.
   */
  useEffect(() => {
    if (open) return
    setFile(null)
    setPreview(null)
    setResult(null)
    setUploadKey(key => key + 1)
  }, [open])

  async function handleDownloadTemplate() {
    try {
      setDownloading(true)
      const { blob, filename } = await downloadContactImportTemplate()
      triggerBlobDownload(blob, filename ?? 'Berater_Import_Vorlage.xlsx')
    } catch (error) {
      console.error('Failed to download contact import template', error)
      toast.error(
        'Vorlage nicht verfügbar',
        getErrorMessage(error, 'Die Vorlage konnte nicht heruntergeladen werden.')
      )
    } finally {
      setDownloading(false)
    }
  }

  async function handleFileAccepted(accepted: File) {
    setFile(accepted)
    setResult(null)
    setPreview(null)

    try {
      setChecking(true)
      setPreview(await previewContactImport(accepted))
    } catch (error) {
      console.error('Failed to preview contact import', error)
      setFile(null)
      toast.error(
        'Datei konnte nicht geprüft werden',
        getErrorMessage(error, 'Bitte verwenden Sie die heruntergeladene Vorlage.')
      )
    } finally {
      setChecking(false)
      // Refresh the picker whatever happened. A file input only fires a change event when the
      // chosen path differs from the last one, so without a remount the central workflow breaks:
      // the SIB sees flagged rows, fixes the spreadsheet, re-picks the same filename — and
      // nothing happens, leaving the stale preview on screen. The chosen name is rendered from
      // `file`, not from the picker, so remounting costs nothing visible.
      setUploadKey(key => key + 1)
    }
  }

  async function handleImport() {
    if (!file) return

    try {
      setImporting(true)
      const imported = await importContacts(file)
      setResult(imported)

      if (imported.imported > 0) {
        toast.success(
          'Import abgeschlossen',
          imported.imported === 1
            ? '1 Berater importiert.'
            : `${imported.imported} Berater importiert.`
        )
        onImported()
      } else {
        toast.info('Import abgeschlossen', 'Es wurden keine neuen Berater angelegt.')
      }

      // Nothing left for the SIB to act on: close rather than make them dismiss a result they
      // already saw in the toast. Rows that failed keep the drawer open, because the result table
      // is the only place that says which ones and why.
      if (imported.failed === 0) {
        onOpenChange(false)
      }
    } catch (error) {
      console.error('Failed to import contacts', error)
      toast.error(
        'Import fehlgeschlagen',
        getErrorMessage(error, 'Der Import konnte nicht abgeschlossen werden.')
      )
    } finally {
      setImporting(false)
    }
  }

  const rows = result?.rows ?? preview?.rows ?? []
  const committed = result !== null

  return (
    <Drawer.Root
      open={open}
      size="xl"
      onOpenChange={details => {
        onOpenChange(details.open)
      }}
    >
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <Drawer.Header>
              <Drawer.Title>Berater importieren</Drawer.Title>
            </Drawer.Header>

            <Drawer.Body>
              <Flex direction="column" gap={8}>
                <Flex direction="column" gap={4}>
                  <Heading size="xl">1. Vorlage herunterladen</Heading>
                  <Text color="fg.muted">
                    Die Vorlage enthält Ihre Leistungsempfänger als Auswahlliste. Bitte wählen Sie
                    den Leistungsempfänger immer aus dieser Liste aus und tragen Sie ihn nicht von
                    Hand ein — nur so kann der Berater eindeutig zugeordnet werden.
                  </Text>
                  <Box>
                    <Button
                      variant="outline"
                      loading={downloading}
                      onClick={handleDownloadTemplate}
                    >
                      <LuDownload />
                      <Text>Vorlage herunterladen</Text>
                    </Button>
                  </Box>
                </Flex>

                <Flex direction="column" gap={4}>
                  <Heading size="xl">2. Ausgefüllte Datei hochladen</Heading>
                  <FileUpload.Root
                    key={uploadKey}
                    maxFiles={1}
                    accept={XLSX_ACCEPT}
                    onFileAccept={e => {
                      const accepted = e.files[0]
                      if (accepted) handleFileAccepted(accepted)
                    }}
                    onFileReject={() =>
                      toast.error(
                        'Ungültige Datei',
                        'Es werden nur Excel-Dateien (.xlsx) unterstützt.'
                      )
                    }
                  >
                    <FileUpload.HiddenInput />
                    <FileUpload.Trigger asChild>
                      <Button variant="outline" loading={checking} disabled={importing}>
                        <LuUpload />
                        <Text>{file ? 'Andere Datei wählen' : 'Datei auswählen'}</Text>
                      </Button>
                    </FileUpload.Trigger>
                  </FileUpload.Root>

                  {file && (
                    <Text fontSize="sm" color="fg.muted">
                      {file.name}
                    </Text>
                  )}
                </Flex>

                {preview && !committed && (
                  <Flex direction="column" gap={4}>
                    <Heading size="xl">3. Prüfen und importieren</Heading>

                    <Alert.Root
                      status={preview.summary.importable > 0 ? 'info' : 'warning'}
                      variant="subtle"
                    >
                      <Alert.Indicator />
                      <Alert.Content>
                        <Alert.Title>
                          {preview.summary.importable} von {preview.summary.total} Zeilen werden
                          importiert
                        </Alert.Title>
                        <Alert.Description>
                          {describeSummary(preview)}
                        </Alert.Description>
                      </Alert.Content>
                    </Alert.Root>

                    <RowTable rows={rows} committed={false} />

                    <HStack justify="flex-end" gap={3}>
                      <Button variant="subtle" onClick={reset} disabled={importing}>
                        Abbrechen
                      </Button>
                      <Button
                        variant="solid"
                        colorPalette="blue"
                        loading={importing}
                        disabled={preview.summary.importable === 0}
                        onClick={handleImport}
                      >
                        {preview.summary.importable === 1
                          ? '1 Berater importieren'
                          : `${preview.summary.importable} Berater importieren`}
                      </Button>
                    </HStack>
                  </Flex>
                )}

                {committed && result && (
                  <Flex direction="column" gap={4}>
                    <Heading size="xl">Ergebnis</Heading>

                    <Alert.Root
                      status={result.failed > 0 ? 'warning' : 'success'}
                      variant="subtle"
                    >
                      <Alert.Indicator />
                      <Alert.Content>
                        <Alert.Title>
                          {result.imported} importiert, {result.skipped} übersprungen,{' '}
                          {result.failed} nicht importiert
                        </Alert.Title>
                        {result.failed > 0 && (
                          <Alert.Description>
                            Korrigieren Sie die betroffenen Zeilen in Ihrer Datei und laden Sie sie
                            erneut hoch. Bereits importierte Berater werden dabei übersprungen.
                          </Alert.Description>
                        )}
                      </Alert.Content>
                    </Alert.Root>

                    <RowTable rows={rows} committed />

                    <HStack justify="flex-end" gap={3}>
                      <Button variant="subtle" onClick={reset}>
                        Weitere Datei importieren
                      </Button>
                      <Button
                        variant="solid"
                        colorPalette="blue"
                        onClick={() => onOpenChange(false)}
                      >
                        Schließen
                      </Button>
                    </HStack>
                  </Flex>
                )}
              </Flex>
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  )
}

/** One line naming only the row groups that actually occur, so a clean file reads "clean". */
function describeSummary(preview: ContactImportPreviewResponse): string {
  const { duplicates, unresolved, invalid } = preview.summary
  const parts: string[] = []

  if (duplicates > 0) parts.push(`${duplicates} bereits vorhanden`)
  if (unresolved > 0) parts.push(`${unresolved} ohne eindeutigen Leistungsempfänger`)
  if (invalid > 0) parts.push(`${invalid} mit ungültigen Daten`)

  if (parts.length === 0) {
    return 'Alle Zeilen konnten zugeordnet werden.'
  }
  return `Nicht importiert werden: ${parts.join(', ')}.`
}

function RowTable({ rows, committed }: { rows: ContactImportRowDto[]; committed: boolean }) {
  if (rows.length === 0) {
    return <Text color="fg.muted">Die Datei enthält keine Zeilen.</Text>
  }

  return (
    <Box overflowX="auto">
      <Table.Root size="sm" minW="820px">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader whiteSpace="nowrap" width="1%">
              Zeile
            </Table.ColumnHeader>
            <Table.ColumnHeader>Berater</Table.ColumnHeader>
            <Table.ColumnHeader whiteSpace="nowrap" width="1%">
              Beraternummer
            </Table.ColumnHeader>
            <Table.ColumnHeader>Leistungsempfänger</Table.ColumnHeader>
            <Table.ColumnHeader whiteSpace="nowrap" width="1%">
              Status
            </Table.ColumnHeader>
            <Table.ColumnHeader width="full">Hinweis</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rows.map(row => {
            // Every field here is typed but arrives as untyped JSON, so this row is rendered
            // defensively throughout: an unknown `status` would make meta undefined and a missing
            // `messages` would throw on .join — either one blanks the whole table, which is the
            // one screen the SIB needs in order to see what went wrong.
            const meta = STATUS_META[row.status] ?? {
              label: row.status,
              committedLabel: row.status,
              palette: 'gray',
            }
            return (
              <Table.Row key={row.rowNumber}>
                <Table.Cell>{row.rowNumber}</Table.Cell>
                <Table.Cell>
                  {`${row.firstName ?? ''} ${row.lastName ?? ''}`.trim() || '—'}
                </Table.Cell>
                <Table.Cell whiteSpace="nowrap">{row.advisorNumber || '—'}</Table.Cell>
                {/* `||`, not `??`: a row that left the Leistungsempfänger blank arrives with an
                    empty string, which nullish coalescing would render as an empty cell rather
                    than the placeholder every neighbouring column shows. */}
                <Table.Cell>{row.resolvedBankName || row.bankName || '—'}</Table.Cell>
                <Table.Cell whiteSpace="nowrap">
                  <Badge colorPalette={meta.palette} variant="subtle">
                    {committed ? meta.committedLabel : meta.label}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <Text fontSize="sm" color="fg.muted">
                    {(row.messages ?? []).join(' ')}
                  </Text>
                </Table.Cell>
              </Table.Row>
            )
          })}
        </Table.Body>
      </Table.Root>
    </Box>
  )
}
